import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/synapse/Icon';
import { cn } from '@/lib/utils';

export interface PatientSearchFieldProps {
  value: string;
  onChange: (v: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  autoFocus?: boolean;
  size?: 'md' | 'lg';
  /** Id of the currently keyboard-highlighted result row, for screen readers. */
  activeDescendantId?: string;
}

/**
 * The patient type-ahead search input — a combobox wired for `aria-activedescendant`
 * keyboard navigation. Shared by the doctor Patients page and the all-roles
 * Registry so both render an identical search affordance.
 */
export function PatientSearchField({
  value,
  onChange,
  inputRef,
  autoFocus,
  size = 'lg',
  activeDescendantId,
}: PatientSearchFieldProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'relative flex items-center gap-2.5 rounded-md border-[1.5px] border-line-2 bg-paper px-3.5 focus-within:border-ink',
        size === 'lg' ? 'h-12' : 'h-11',
      )}
    >
      <Icon name="search" size={18} className="shrink-0 text-muted" />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        placeholder={t('patients.searchPlaceholder')}
        aria-label={t('patients.searchLabel')}
        role="combobox"
        aria-expanded="true"
        aria-controls="patient-results"
        aria-activedescendant={activeDescendantId}
        className="min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-muted-2"
      />
      {value && (
        <button
          type="button"
          aria-label={t('patients.clearSearch')}
          onClick={() => {
            onChange('');
            inputRef.current?.focus();
          }}
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-paper-3 text-muted hover:text-ink"
        >
          <Icon name="x" size={14} />
        </button>
      )}
    </div>
  );
}
